<template>
  <Modal @close="$emit('close')" size="md">
    <template #body>
      <div class="p-6">
        <h2 class="text-2xl font-bold text-center mb-6">Sign In to Continue</h2>
        
        <div v-if="errorMessage" class="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {{ errorMessage }}
        </div>

        <form @submit.prevent="handleLogin" class="space-y-4">
          <div>
            <label for="modal-email" class="block text-sm font-medium text-gray-700 mb-1">
              Email address
            </label>
            <input 
              id="modal-email" 
              v-model="email" 
              type="email" 
              autocomplete="email" 
              required 
              class="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-pink-500 focus:border-pink-500"
              placeholder="Enter your email"
            >
          </div>

          <div>
            <label for="modal-password" class="block text-sm font-medium text-gray-700 mb-1">
              Password
            </label>
            <input 
              id="modal-password" 
              v-model="password" 
              type="password" 
              autocomplete="current-password" 
              required 
              class="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-pink-500 focus:border-pink-500"
              placeholder="Enter your password"
            >
          </div>

          <div class="flex items-center justify-between">
            <div class="flex items-center">
              <input 
                id="modal-remember-me" 
                v-model="rememberMe" 
                type="checkbox" 
                class="h-4 w-4 text-pink-600 focus:ring-pink-500 border-gray-300 rounded"
              >
              <label for="modal-remember-me" class="ml-2 block text-sm text-gray-700">
                Remember me
              </label>
            </div>

            <div class="text-sm">
              <a href="/forgot-password" class="font-medium text-pink-600 hover:text-pink-500">
                Forgot password?
              </a>
            </div>
          </div>

          <div>
            <button 
              type="submit" 
              :disabled="isLoading"
              class="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-pink-600 hover:bg-pink-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-pink-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <span v-if="!isLoading">Sign in</span>
              <span v-else class="flex items-center">
                <svg class="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Signing in...
              </span>
            </button>
          </div>
        </form>

        <div class="mt-6">
          <div class="relative">
            <div class="absolute inset-0 flex items-center">
              <div class="w-full border-t border-gray-300"></div>
            </div>
            <div class="relative flex justify-center text-sm">
              <span class="px-2 bg-white text-gray-500">Or continue with</span>
            </div>
          </div>

          <div class="mt-6 grid grid-cols-2 gap-3">
            <a href="/auth/facebook" class="w-full inline-flex justify-center py-2 px-4 border border-gray-300 rounded-md shadow-sm bg-white text-sm font-medium text-gray-500 hover:bg-gray-50">
              <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M20 10c0-5.523-4.477-10-10-10S0 4.477 0 10c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V10h2.54V7.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V10h2.773l-.443 2.89h-2.33v6.988C16.343 19.128 20 14.991 20 10z" clip-rule="evenodd"/>
              </svg>
              <span class="ml-2">Facebook</span>
            </a>

            <a href="/auth/google" class="w-full inline-flex justify-center py-2 px-4 border border-gray-300 rounded-md shadow-sm bg-white text-sm font-medium text-gray-500 hover:bg-gray-50">
              <svg class="w-5 h-5" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              <span class="ml-2">Google</span>
            </a>
          </div>
        </div>

        <div class="mt-6 text-center text-sm">
          <span class="text-gray-600">Don't have an account?</span>
          <a href="/register" class="font-medium text-pink-600 hover:text-pink-500 ml-1">
            Sign up
          </a>
        </div>
      </div>
    </template>
  </Modal>
</template>

<script>
import Modal from '../ui/Modal.vue';
import axios from 'axios';

export default {
  name: 'LoginModal',
  
  components: {
    Modal
  },
  
  data() {
    return {
      email: '',
      password: '',
      rememberMe: false,
      isLoading: false,
      errorMessage: ''
    };
  },
  
  methods: {
    async handleLogin() {
      this.isLoading = true;
      this.errorMessage = '';
      
      try {
        // Get CSRF token from meta tag or existing cookie
        const token = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || 
                     document.querySelector('input[name="_token"]')?.value;
        
        const response = await axios.post('/login', {
          email: this.email,
          password: this.password,
          remember: this.rememberMe,
          _token: token
        });
        
        if (response.data.success || response.status === 200) {
          // Emit success event with user data if available
          this.$emit('login-success', response.data.user || {});
          this.$emit('close');
        } else {
          this.errorMessage = response.data.message || 'Login failed. Please try again.';
        }
      } catch (error) {
        if (error.response && error.response.status === 422) {
          // Validation errors
          const errors = error.response.data.errors;
          if (errors) {
            this.errorMessage = Object.values(errors).flat().join(' ');
          } else {
            this.errorMessage = error.response.data.message || 'Invalid email or password.';
          }
        } else if (error.response && error.response.status === 401) {
          this.errorMessage = 'Invalid email or password.';
        } else {
          this.errorMessage = 'An error occurred. Please try again.';
        }
      } finally {
        this.isLoading = false;
      }
    }
  },
  
  mounted() {
    // Focus on email input when modal opens
    this.$nextTick(() => {
      document.getElementById('modal-email')?.focus();
    });
  }
};
</script>